import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Button,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip
} from '@mui/material';
import {
    Person as PersonIcon,
    Share as ShareIcon,
    BarChart as BarChartIcon,
    ArrowBack as ArrowBackIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Deck as DeckIcon,
    MenuBook as MenuBookIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { getArchetype, getAggregatedArchetypeStats, getDecksByArchetype, getExpansionSets, getPreferredTrackerSetId, normalizeTrackerSetId, TRACKER_DEFAULT_SET_ID, updateUserTrackerContext, getArchetypeGuide, saveArchetypeGuide, checkCanEditArchetypeGuide } from '../../services/tracker-limited-data';
import { useAuth } from '../../contexts/AuthContext';
import { InkColorIcons } from './InkColorIcon';
import DeckVisual from './DeckVisual';
import GuideRenderer from './GuideRenderer';
import GuideEditor from './GuideEditor';

const cardStyle = {
    background: 'rgba(17, 24, 39, 0.85)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(76, 29, 149, 0.25)',
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
        borderColor: 'rgba(124, 58, 237, 0.4)',
        boxShadow: '0 0 20px rgba(124, 58, 237, 0.08)'
    }
};

const PAGE_VIEWS = { stats: 'stats', guide: 'guide' };

function GuideView({ archetypeId, isAdmin, vsArchetypes, trackerFilters, archetypeInkColors }) {
    const [guide, setGuide] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editorValue, setEditorValue] = useState('');
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getArchetypeGuide(archetypeId)
            .then((data) => {
                if (!cancelled) {
                    setGuide(data?.guide || null);
                    setEditorValue(data?.guide?.content?.body || '');
                }
            })
            .catch(() => {
                if (!cancelled) setGuide(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [archetypeId]);

    const handleEdit = () => {
        const content = guide?.content;
        if (content?.schema_version === 1) {
            setEditorValue(JSON.stringify(content, null, 2));
        } else {
            setEditorValue(content?.body || '');
        }
        setSaveError(null);
        setEditing(true);
    };

    const handleCancel = () => {
        setEditing(false);
        setSaveError(null);
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveError(null);
        let contentToSave;
        const trimmed = editorValue.trim();
        if (trimmed.startsWith('{')) {
            try {
                contentToSave = JSON.parse(trimmed);
            } catch {
                setSaveError('Nieprawidłowy JSON. Sprawdź składnię i spróbuj ponownie.');
                setSaving(false);
                return;
            }
        } else {
            contentToSave = { body: editorValue };
        }
        try {
            await saveArchetypeGuide(archetypeId, contentToSave);
            setGuide((prev) => ({ ...(prev || {}), content: contentToSave }));
            setEditing(false);
        } catch (err) {
            console.error('[GuideView] save error:', err);
            setSaveError(`Błąd zapisu: ${err?.message || 'Nieznany błąd'}`);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: '#7c3aed' }} size={32} />
            </Box>
        );
    }

    const guideContent = guide?.content;
    const isStructured = guideContent?.schema_version === 1;
    const guideBody = guideContent?.body || '';
    const hasContent = isStructured || guideBody.trim().length > 0;

    if (editing) {
        if (isStructured || !hasContent) {
            return (
                <GuideEditor
                    initialContent={isStructured ? guideContent : null}
                    trackerFilters={trackerFilters}
                    archetypeId={archetypeId}
                    archetypeInkColors={archetypeInkColors || []}
                    onSave={async (contentToSave) => {
                        setSaving(true);
                        setSaveError(null);
                        try {
                            await saveArchetypeGuide(archetypeId, contentToSave);
                            setGuide((prev) => ({ ...(prev || {}), content: contentToSave }));
                            setEditing(false);
                        } catch (err) {
                            setSaveError(`Błąd zapisu: ${err?.message || 'Nieznany błąd'}`);
                        } finally {
                            setSaving(false);
                        }
                    }}
                    onCancel={handleCancel}
                    saving={saving}
                    saveError={saveError}
                />
            );
        }
        return (
            <Card sx={{ ...cardStyle }}>
                <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.9)', fontWeight: 600, fontSize: '0.85rem' }}>
                            Edytuj poradnik (legacy)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                                onClick={handleCancel}
                                disabled={saving}
                                sx={{
                                    color: 'rgba(196, 181, 253, 0.7)',
                                    borderColor: 'rgba(76, 29, 149, 0.4)',
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    '&:hover': { borderColor: 'rgba(124, 58, 237, 0.6)', bgcolor: 'rgba(124, 58, 237, 0.06)' }
                                }}
                            >
                                Anuluj
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                onClick={handleSave}
                                disabled={saving}
                                sx={{
                                    background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    py: 0.5,
                                    '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #5b21b6)' }
                                }}
                            >
                                {saving ? 'Zapisuję...' : 'Zapisz'}
                            </Button>
                        </Box>
                    </Box>
                    {saveError && (
                        <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>{saveError}</Alert>
                    )}
                    <TextField
                        multiline
                        fullWidth
                        minRows={12}
                        maxRows={30}
                        value={editorValue}
                        onChange={(e) => setEditorValue(e.target.value)}
                        placeholder="Napisz poradnik dla tego archetypu... (markdown)"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.85rem',
                                fontFamily: 'monospace',
                                bgcolor: 'rgba(0,0,0,0.25)',
                                '& fieldset': { borderColor: 'rgba(76, 29, 149, 0.4)' },
                                '&:hover fieldset': { borderColor: 'rgba(124, 58, 237, 0.6)' },
                                '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
                            }
                        }}
                    />
                    <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.4)', mt: 0.75, display: 'block' }}>
                        Edytujesz poradnik w trybie legacy (markdown).
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ ...cardStyle }}>
            <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: hasContent ? 2 : 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MenuBookIcon sx={{ fontSize: 18, color: 'rgba(196, 181, 253, 0.7)' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.9)', fontWeight: 600, fontSize: '0.85rem' }}>
                            Poradnik
                        </Typography>
                        {hasContent && (
                            <Chip
                                label="Dostępny"
                                size="small"
                                sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: 'rgba(16, 185, 129, 0.15)',
                                    color: '#10b981',
                                    border: '1px solid rgba(16, 185, 129, 0.3)',
                                    '& .MuiChip-label': { px: 0.75 }
                                }}
                            />
                        )}
                    </Box>
                    {isAdmin && (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                            onClick={handleEdit}
                            sx={{
                                color: 'rgba(196, 181, 253, 0.8)',
                                borderColor: 'rgba(76, 29, 149, 0.4)',
                                textTransform: 'none',
                                fontSize: '0.75rem',
                                py: 0.5,
                                '&:hover': { borderColor: 'rgba(124, 58, 237, 0.6)', bgcolor: 'rgba(124, 58, 237, 0.06)' }
                            }}
                        >
                            {hasContent ? 'Edytuj' : 'Utwórz poradnik'}
                        </Button>
                    )}
                </Box>

                {hasContent ? (
                    isStructured ? (
                        <GuideRenderer content={guideContent} vsArchetypes={vsArchetypes} trackerFilters={trackerFilters} />
                    ) : (
                        <Box
                            sx={{
                                color: 'rgba(255,255,255,0.85)',
                                fontSize: '0.9rem',
                                lineHeight: 1.7,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                '& h1, & h2, & h3': { color: '#c4b5fd', mt: 2, mb: 0.5 },
                                '& strong': { color: 'white' },
                                '& em': { color: 'rgba(196, 181, 253, 0.85)' }
                            }}
                        >
                            {guideBody}
                        </Box>
                    )
                ) : (
                    <Box sx={{ py: 5, textAlign: 'center' }}>
                        <MenuBookIcon sx={{ fontSize: 48, color: 'rgba(76, 29, 149, 0.4)', mb: 1.5, display: 'block', mx: 'auto' }} />
                        <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.5)', fontSize: '0.85rem' }}>
                            Brak poradnika dla tego archetypu.
                        </Typography>
                        {!isAdmin && (
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.35)', mt: 0.5, display: 'block' }}>
                                Poradnik zostanie dodany przez administratora serwisu.
                            </Typography>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

function ArchetypeDetailsPage() {
    const { archetypeId } = useParams();
    const { currentUser, userData, refreshUserData, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [archetype, setArchetype] = useState(null);
    const [archetypeStats, setArchetypeStats] = useState(null);
    const [decks, setDecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('archetypes');
    const [selectedFormat, setSelectedFormat] = useState('core_constructed');
    const [selectedSetId, setSelectedSetId] = useState(TRACKER_DEFAULT_SET_ID);
    const [expansionSets, setExpansionSets] = useState([]);
    const [contextReady, setContextReady] = useState(false);
    const [pageView, setPageView] = useState(PAGE_VIEWS.stats);
    const [canEditGuide, setCanEditGuide] = useState(false);

    const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin' || canEditGuide;

    useEffect(() => {
        if (!currentUser || !archetypeId) return;
        if (userData?.role === 'admin' || userData?.role === 'superadmin') {
            setCanEditGuide(true);
            return;
        }
        checkCanEditArchetypeGuide(archetypeId).then(setCanEditGuide).catch(() => setCanEditGuide(false));
    }, [currentUser, archetypeId, userData?.role]);

    const TRACKER_CONTEXT_STORAGE_KEY = 'tracker:selectedContext';
    const isValidTrackerFormat = (value) => value === 'core_constructed' || value === 'infinity';

    const trackerFilters = useMemo(() => ({
        format: selectedFormat,
        setId: selectedSetId || null
    }), [selectedFormat, selectedSetId]);

    const persistTrackerContext = useCallback((format, setId) => {
        const nextSetId = normalizeTrackerSetId(setId) || TRACKER_DEFAULT_SET_ID;
        try {
            window.localStorage.setItem(
                TRACKER_CONTEXT_STORAGE_KEY,
                JSON.stringify({ format, setId: nextSetId })
            );
        } catch (error) {
            console.warn('Nie udało się zapisać kontekstu trackera do localStorage:', error);
        }
        if (!currentUser) return;
        updateUserTrackerContext(currentUser.uid, {
            format,
            setId: nextSetId
        })
            .then(() => refreshUserData?.())
            .catch((error) => {
                console.warn('Nie udało się zapisać kontekstu trackera w profilu użytkownika:', error);
            });
    }, [currentUser, refreshUserData]);

    const handleFormatChange = (event) => {
        const nextFormat = event.target.value;
        setSelectedFormat(nextFormat);
        persistTrackerContext(nextFormat, selectedSetId);
    };

    const handleSetChange = (event) => {
        const nextSetId = normalizeTrackerSetId(event.target.value) || TRACKER_DEFAULT_SET_ID;
        setSelectedSetId(nextSetId);
        persistTrackerContext(selectedFormat, nextSetId);
    };

    useEffect(() => {
        if (authLoading || (currentUser && !userData)) {
            setContextReady(false);
            return;
        }

        let nextFormat = 'core_constructed';
        let nextSetId = TRACKER_DEFAULT_SET_ID;

        if (currentUser) {
            if (isValidTrackerFormat(userData?.trackerSelectedFormat)) {
                nextFormat = userData.trackerSelectedFormat;
            }
            nextSetId = normalizeTrackerSetId(userData?.trackerSelectedSetId) || TRACKER_DEFAULT_SET_ID;
        } else {
            try {
                const raw = window.localStorage.getItem(TRACKER_CONTEXT_STORAGE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (isValidTrackerFormat(parsed?.format)) {
                        nextFormat = parsed.format;
                    }
                    nextSetId = normalizeTrackerSetId(parsed?.setId) || nextSetId;
                }
            } catch (error) {
                console.warn('Nie udało się odczytać kontekstu trackera z localStorage:', error);
            }
        }

        setSelectedFormat(nextFormat);
        setSelectedSetId(nextSetId);
        setContextReady(true);
    }, [authLoading, currentUser, userData]);

    useEffect(() => {
        const loadArchetypeDetails = async () => {
            if (!contextReady) return;
            setLoading(true);
            setError(null);
            try {
                const archetypeData = await getArchetype(archetypeId, trackerFilters);
                if (!archetypeData) {
                    setError('Archetyp nie został znaleziony');
                    return;
                }
                setArchetype(archetypeData);
                const [stats, archetypeDecks] = await Promise.all([
                    currentUser ? getAggregatedArchetypeStats(currentUser.uid, trackerFilters) : Promise.resolve({}),
                    getDecksByArchetype(archetypeId, currentUser?.uid, trackerFilters)
                ]);
                setArchetypeStats(stats?.[archetypeId] || null);
                setDecks(archetypeDecks);
            } catch (err) {
                console.error('Błąd podczas ładowania danych archetypu:', err);
                setError('Wystąpił błąd podczas ładowania danych archetypu');
            } finally {
                setLoading(false);
            }
        };
        loadArchetypeDetails();
    }, [archetypeId, contextReady, currentUser, trackerFilters]);

    useEffect(() => {
        const loadSets = async () => {
            try {
                const sets = await getExpansionSets();
                setExpansionSets(sets);
                if (!contextReady) return;
                const preferredSetId = getPreferredTrackerSetId(sets, selectedSetId);
                if (preferredSetId && preferredSetId !== selectedSetId) {
                    setSelectedSetId(preferredSetId);
                    persistTrackerContext(selectedFormat, preferredSetId);
                }
            } catch (err) {
                console.error('Błąd podczas ładowania setów archetypu:', err);
            }
        };
        loadSets();
    }, [contextReady, persistTrackerContext, selectedFormat, selectedSetId]);

    useEffect(() => {
        if (!contextReady) return;
        try {
            window.localStorage.setItem(
                TRACKER_CONTEXT_STORAGE_KEY,
                JSON.stringify({
                    format: selectedFormat,
                    setId: normalizeTrackerSetId(selectedSetId) || TRACKER_DEFAULT_SET_ID
                })
            );
        } catch (error) {
            console.warn('Nie udało się zapisać kontekstu trackera do localStorage:', error);
        }
    }, [contextReady, selectedFormat, selectedSetId]);

    useEffect(() => {
        if (!archetype) return;
        const vsA = Object.keys(archetypeStats?.vsArchetypes || {}).length > 0;
        const perP = Object.keys(archetypeStats?.perPlayer || {}).length > 0;
        const hasDecks = decks.length > 0;
        const tabOrder = [vsA && 'archetypes', perP && 'players', hasDecks && 'decks'].filter(Boolean);
        if (tabOrder.length && !tabOrder.includes(activeTab)) {
            setActiveTab(tabOrder[0]);
        }
    }, [archetype, archetypeStats, decks.length]);

    const getWinrateColor = (winrate) => {
        if (winrate >= 60) return '#10b981';
        if (winrate >= 50) return '#f59e0b';
        return '#ef4444';
    };

    const getWinrateBg = (winrate) => {
        if (winrate >= 60) return 'rgba(16, 185, 129, 0.15)';
        if (winrate >= 50) return 'rgba(245, 158, 11, 0.12)';
        return 'rgba(239, 68, 68, 0.15)';
    };

    const formatColorName = (color) => {
        const map = { amber: 'Amber', amethyst: 'Amethyst', steel: 'Steel', ruby: 'Ruby', sapphire: 'Sapphire', emerald: 'Emerald' };
        return map[color] || color;
    };

    const handleShare = () => navigator.clipboard.writeText(window.location.href);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress sx={{ color: '#7c3aed' }} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                <Button onClick={() => navigate(-1)} variant="outlined">
                    Powrót
                </Button>
            </Container>
        );
    }

    if (!archetype) return null;

    const vsArchetypes = archetypeStats?.vsArchetypes || {};
    const perPlayer = archetypeStats?.perPlayer || {};
    const overall = archetypeStats
        ? {
            games: Object.values(vsArchetypes).reduce((s, x) => s + (x.games || 0), 0),
            wins: Object.values(vsArchetypes).reduce((s, x) => s + (x.wins || 0), 0),
            losses: Object.values(vsArchetypes).reduce((s, x) => s + (x.losses || 0), 0),
            winrate: 0
        }
        : { games: 0, wins: 0, losses: 0, winrate: 0 };

    if (overall.games > 0) {
        overall.winrate = Math.round((overall.wins / overall.games) * 100);
    }

    const hasVsArchetypes = Object.keys(vsArchetypes).length > 0;
    const hasPerPlayer = Object.keys(perPlayer).length > 0;
    const hasDecks = decks.length > 0;

    const tabOrder = [hasVsArchetypes && 'archetypes', hasPerPlayer && 'players', hasDecks && 'decks'].filter(Boolean);
    const defaultTab = tabOrder[0] || 'archetypes';

    return (
        <Container maxWidth="lg" sx={{ py: isMobile ? 1.5 : 2, px: isSmallMobile ? 1.5 : 2 }}>
            {/* Header */}
            <Card sx={{ ...cardStyle, mb: 2, p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{ color: 'rgba(196, 181, 253, 0.9)', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}
                        size="medium"
                    >
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </IconButton>
                    <Box
                        sx={{
                            width: isSmallMobile ? 48 : 56,
                            height: isSmallMobile ? 48 : 56,
                            borderRadius: '10px',
                            flexShrink: 0,
                            bgcolor: 'rgba(76, 29, 149, 0.15)',
                            border: '1px solid rgba(76, 29, 149, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <InkColorIcons colors={archetype.ink_colors || []} size={isSmallMobile ? 26 : 30} gap={0.4} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: isSmallMobile ? '1rem' : '1.15rem', letterSpacing: '-0.01em' }} noWrap>
                            {archetype.name}
                        </Typography>
                        {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.85)', fontSize: '0.8rem' }}>
                                {archetype.ink_colors.map(formatColorName).join(' / ')}
                            </Typography>
                        )}
                    </Box>

                    {/* Segmented control: Statystyki / Poradnik */}
                    <Box
                        sx={{
                            display: 'flex',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            border: '1px solid rgba(76, 29, 149, 0.35)',
                            borderRadius: '8px',
                            p: '3px',
                            gap: '3px',
                            flexShrink: 0
                        }}
                    >
                        {[
                            { id: PAGE_VIEWS.stats, icon: <BarChartIcon sx={{ fontSize: 16 }} />, label: isSmallMobile ? null : 'Statystyki' },
                            { id: PAGE_VIEWS.guide, icon: <MenuBookIcon sx={{ fontSize: 16 }} />, label: isSmallMobile ? null : 'Poradnik' }
                        ].map(({ id, icon, label }) => {
                            const active = pageView === id;
                            return (
                                <Box
                                    key={id}
                                    onClick={() => setPageView(id)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: label ? 0.5 : 0,
                                        px: label ? 1.25 : 1,
                                        py: 0.5,
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        bgcolor: active ? 'rgba(124, 58, 237, 0.35)' : 'transparent',
                                        border: active ? '1px solid rgba(124, 58, 237, 0.5)' : '1px solid transparent',
                                        color: active ? '#c4b5fd' : 'rgba(196, 181, 253, 0.5)',
                                        '&:hover': {
                                            bgcolor: active ? 'rgba(124, 58, 237, 0.35)' : 'rgba(124, 58, 237, 0.1)',
                                            color: active ? '#c4b5fd' : 'rgba(196, 181, 253, 0.8)'
                                        }
                                    }}
                                >
                                    {icon}
                                    {label && (
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: active ? 600 : 400, lineHeight: 1 }}>
                                            {label}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Tooltip title="Udostępnij">
                        <IconButton onClick={handleShare} size="medium" sx={{ color: 'rgba(196, 181, 253, 0.8)', '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.15)' } }}>
                            <ShareIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Card>

            {/* Widok: Statystyki */}
            {pageView === PAGE_VIEWS.stats && (
                <>
                    <Card sx={{ ...cardStyle, mb: 2, p: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Format</InputLabel>
                                <Select
                                    value={selectedFormat}
                                    label="Format"
                                    onChange={handleFormatChange}
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.8)' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' }
                                    }}
                                >
                                    <MenuItem value="core_constructed">Core Constructed</MenuItem>
                                    <MenuItem value="infinity">Infinity</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel sx={{ color: 'rgba(196, 181, 253, 0.7)' }}>Set</InputLabel>
                                <Select
                                    value={selectedSetId}
                                    label="Set"
                                    onChange={handleSetChange}
                                    sx={{
                                        color: 'white',
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.8)' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' }
                                    }}
                                >
                                    {expansionSets.map((setItem) => (
                                        <MenuItem key={setItem.id} value={setItem.id}>
                                            {setItem.name || setItem.id}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Card>

                    {/* Statystyki */}
                    {archetypeStats && (
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                            <Grid item xs={3}>
                                <Card sx={{ ...cardStyle, height: '100%' }}>
                                    <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                                        <Typography variant="h5" sx={{ color: getWinrateColor(overall.winrate), fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>
                                            {overall.winrate}%
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Winrate
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={3}>
                                <Card sx={{ ...cardStyle, height: '100%' }}>
                                    <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>{overall.games}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gier</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={3}>
                                <Card sx={{ ...cardStyle, height: '100%' }}>
                                    <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            <TrendingUpIcon sx={{ color: '#10b981', fontSize: 22 }} />
                                            <Typography variant="h5" sx={{ color: '#10b981', fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>{overall.wins}</Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Wygrane</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={3}>
                                <Card sx={{ ...cardStyle, height: '100%' }}>
                                    <CardContent sx={{ p: 1.5, textAlign: 'center', '&:last-child': { pb: 1.5 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            <TrendingDownIcon sx={{ color: '#ef4444', fontSize: 22 }} />
                                            <Typography variant="h5" sx={{ color: '#ef4444', fontWeight: 700, fontSize: isSmallMobile ? '1.35rem' : '1.5rem', letterSpacing: '-0.02em' }}>{overall.losses}</Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.75)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Przegrane</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {!archetypeStats && (
                        <Card sx={{ ...cardStyle, py: 4 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(196, 181, 253, 0.7)', textAlign: 'center' }}>
                                Brak statystyk. Rozpocznij grę z taliami tego archetypu.
                            </Typography>
                        </Card>
                    )}

                    {/* Tabele i Talie */}
                    {(hasVsArchetypes || hasPerPlayer || hasDecks) && (
                        <Card sx={{ ...cardStyle, overflow: 'hidden' }}>
                            <Tabs
                                value={tabOrder.includes(activeTab) ? activeTab : defaultTab}
                                onChange={(_, v) => setActiveTab(v)}
                                sx={{
                                    minHeight: 40,
                                    px: 1,
                                    borderBottom: '1px solid rgba(76, 29, 149, 0.2)',
                                    '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.8rem', color: 'rgba(196, 181, 253, 0.6)', textTransform: 'none', fontWeight: 500 },
                                    '& .Mui-selected': { color: '#c4b5fd' },
                                    '& .MuiTabs-indicator': { backgroundColor: '#7c3aed', height: 2 }
                                }}
                            >
                                {hasVsArchetypes && <Tab value="archetypes" icon={<BarChartIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="vs Archetypy" />}
                                {hasPerPlayer && <Tab value="players" icon={<PersonIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Per Gracz" />}
                                {hasDecks && <Tab value="decks" icon={<DeckIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Talie" />}
                            </Tabs>
                            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                {activeTab === 'archetypes' && hasVsArchetypes && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Archetyp</TableCell>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Play</TableCell>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Draw</TableCell>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gier</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(vsArchetypes).sort(([, a], [, b]) => b.games - a.games).map(([oppId, s]) => {
                                                    const isLowSample = (s.games || 0) < 5;
                                                    return (
                                                    <TableRow
                                                        key={oppId}
                                                        onClick={() => navigate(`/archetype/${oppId}`)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            bgcolor: isLowSample ? 'rgba(107, 114, 128, 0.12)' : getWinrateBg(s.winrate),
                                                            opacity: isLowSample ? 0.85 : 1,
                                                            borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                                                            transition: 'background 0.15s ease',
                                                            '&:hover': { filter: 'brightness(1.1)' },
                                                            '&:last-child': { borderBottom: 'none' }
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'rgba(0, 0, 0, 0.35)', borderRadius: 1, px: 0.75, py: 0.25, border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                                                                        <InkColorIcons colors={s.colors || []} size={24} gap={0.4} />
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ color: isLowSample ? 'rgba(196, 181, 253, 0.6)' : 'white', fontSize: '0.8rem', fontWeight: 600 }} noWrap>{s.name}</Typography>
                                                                        {isLowSample && <Typography variant="caption" sx={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.65rem', display: 'block' }}>Za mało meczy (min. 5)</Typography>}
                                                                    </Box>
                                                                </Box>
                                                                <Typography component="span" sx={{ color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : getWinrateColor(s.winrate), fontWeight: isLowSample ? 500 : 700, fontSize: '0.85rem', flexShrink: 0 }}>
                                                                    {s.winrate}%
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.75)' : 'rgba(255,255,255,0.9)', fontSize: '0.8rem', py: 1 }}>{s.play?.winrate ?? '—'}%</TableCell>
                                                        <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.75)' : 'rgba(255,255,255,0.9)', fontSize: '0.8rem', py: 1 }}>{s.draw?.winrate ?? '—'}%</TableCell>
                                                        <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : 'white', fontWeight: 600, fontSize: '0.8rem', py: 1 }}>{s.games}</TableCell>
                                                    </TableRow>
                                                );})}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                                {activeTab === 'players' && hasPerPlayer && (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gracz</TableCell>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>W/L</TableCell>
                                                    <TableCell sx={{ bgcolor: 'rgba(17, 24, 39, 0.98)', color: 'rgba(196, 181, 253, 0.7)', fontWeight: 600, fontSize: '0.75rem', textAlign: 'center', py: 0.75, borderBottom: '1px solid rgba(76, 29, 149, 0.2)' }}>Gier</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(perPlayer).sort(([, a], [, b]) => b.games - a.games).map(([playerId, s]) => {
                                                    const isLowSample = (s.games || 0) < 5;
                                                    return (
                                                    <TableRow
                                                        key={playerId}
                                                        sx={{
                                                            bgcolor: isLowSample ? 'rgba(107, 114, 128, 0.12)' : getWinrateBg(s.winrate),
                                                            opacity: isLowSample ? 0.85 : 1,
                                                            borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                                                            '&:hover': { filter: 'brightness(1.1)' },
                                                            '&:last-child': { borderBottom: 'none' }
                                                        }}
                                                    >
                                                        <TableCell sx={{ py: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{ color: isLowSample ? 'rgba(196, 181, 253, 0.6)' : 'white', fontSize: '0.8rem', fontWeight: 600 }}>{s.name}</Typography>
                                                                    {isLowSample && <Typography variant="caption" sx={{ color: 'rgba(156, 163, 175, 0.9)', fontSize: '0.65rem', display: 'block' }}>Za mało meczy (min. 5)</Typography>}
                                                                </Box>
                                                                <Typography component="span" sx={{ color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : getWinrateColor(s.winrate), fontWeight: isLowSample ? 500 : 700, fontSize: '0.85rem' }}>
                                                                    {s.winrate}%
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.75)' : 'rgba(255,255,255,0.9)', fontSize: '0.8rem', py: 1 }}>{s.wins} / {s.losses}</TableCell>
                                                        <TableCell sx={{ textAlign: 'center', color: isLowSample ? 'rgba(156, 163, 175, 0.85)' : 'white', fontWeight: 600, fontSize: '0.8rem', py: 1 }}>{s.games}</TableCell>
                                                    </TableRow>
                                                );})}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                                {activeTab === 'decks' && hasDecks && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        {decks.map((deck) => {
                                            const deckGames = deck.stats?.overall?.games || 0;
                                            const deckWinrate = deck.stats?.overall?.games
                                                ? Math.round((deck.stats.overall.wins / deck.stats.overall.games) * 100)
                                                : null;
                                            return (
                                                <Box
                                                    key={deck.id}
                                                    onClick={() => navigate(`/tracker/${deck.id}`)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        px: 1.5,
                                                        py: 1,
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                                                        transition: 'background 0.15s ease',
                                                        '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.12)' },
                                                        '&:last-child': { borderBottom: 'none' }
                                                    }}
                                                >
                                                    <Box sx={{ width: 36, height: 36, borderRadius: 1, overflow: 'hidden', bgcolor: 'rgba(76, 29, 149, 0.15)' }}>
                                                        <DeckVisual deck={deck} mode="thumbnail" />
                                                    </Box>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" sx={{ color: 'white', fontWeight: 500, fontSize: '0.85rem' }} noWrap>
                                                            {deck.name}
                                                        </Typography>
                                                        {deckGames > 0 && (
                                                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.7)', fontSize: '0.7rem' }}>
                                                                {deckGames} gier
                                                                {deckWinrate != null && ` · ${deckWinrate}%`}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* Widok: Poradnik */}
            {pageView === PAGE_VIEWS.guide && (
                <GuideView archetypeId={archetypeId} isAdmin={isAdmin} vsArchetypes={vsArchetypes} trackerFilters={trackerFilters} archetypeInkColors={archetype?.ink_colors || []} />
            )}
        </Container>
    );
}

export default ArchetypeDetailsPage;
